import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, EllipsisVertical, Pencil, PlusCircle, Trash2 } from 'lucide-react';
import SpinnerLoad from '../moduleComponents/SpinnerLoad';

function formatUpdated(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function ProjectCardMenu({ onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <button
        type="button"
        className="p-2 rounded-full text-gray-500 hover:bg-gray-100"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Project actions"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <EllipsisVertical className="h-5 w-5" strokeWidth={2} />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-40 min-w-[160px] rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50"
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
          >
            <Pencil className="h-4 w-4" strokeWidth={2} />
            Edit
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-red-700 hover:bg-red-50"
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            <Trash2 className="h-4 w-4" strokeWidth={2} />
            Delete
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function ViewProjectsList({
  projects,
  loading,
  onOpenBoard,
  onCreateClick,
  onEditClick,
  onDeleteClick,
}) {
  if (loading) {
    return (
      <div className="py-8">
        <SpinnerLoad />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-2 pb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Your projects</h2>
        <button
          type="button"
          onClick={onCreateClick}
          className="flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900"
        >
          <PlusCircle className="h-6 w-6" strokeWidth={2} />
          New
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-300 p-8 text-center text-gray-500 text-sm">
          No projects yet. Create one to use the Kanban board.
        </div>
      ) : (
        <ul className="space-y-2">
          {projects.map((p) => {
            const updated = formatUpdated(p.updated_at);
            return (
              <li key={p.id}>
                <div className="flex items-center rounded-xl bg-white shadow">
                  <button
                    type="button"
                    onClick={() => onOpenBoard(p)}
                    className="flex-1 min-w-0 text-left px-4 py-4 hover:bg-gray-50 rounded-l-xl transition"
                  >
                    <h3 className="text-md font-semibold text-gray-900 truncate">{p.name}</h3>
                    {p.description ? (
                      <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{p.description}</p>
                    ) : null}
                    {updated ? (
                      <p className="text-xs text-gray-500 mt-1.5">Updated {updated}</p>
                    ) : null}
                  </button>
                  <ProjectCardMenu onEdit={() => onEditClick(p)} onDelete={() => onDeleteClick(p)} />
                  <button
                    type="button"
                    onClick={() => onOpenBoard(p)}
                    aria-label="Open board"
                    className="self-stretch flex items-center justify-center px-3 text-blue-700 hover:bg-gray-50 rounded-r-xl"
                  >
                    <ChevronRight className="h-6 w-6" strokeWidth={2} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
